import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import CountdownTimer from '../components/CountdownTimer';
import QuestionRenderer from '../components/QuestionRenderer';
import { useAssessment } from '../context/AssessmentContext';
import { useWebcamDetections } from '../Detections';
import { uploadDetectionSummary } from '../api/assessments';

const normalizeQuestion = (payload) => {
  if (!payload || !payload.section) {
    return null;
  }



  const options = Array.isArray(payload.options)
    ? payload.options.map((option) => {
        if (typeof option === 'string') {
          return { label: option, value: option };
        }
        return {
          label: option.label || option.value,
          value: option.value || option.label,
        };
      })
    : null;

  let type = 'text';
  if (Array.isArray(options) && options.length) {
    type = 'mcq';
  } else if (payload.section === 'coding') {
    type = 'coding';
  } else if (payload.section === 'subjective') {
    type = 'subjective';
  }

  return {
    questionId: payload.questionId,
    prompt: payload.prompt,
    questionNumber: payload.questionNumber,
    section: payload.section,
    options,
    type,
    metadata: payload.metadata || {},
    description: payload.metadata?.description || payload.prompt || payload.metadata?.title,
    signature: payload.signature || payload.metadata?.signature,
    supportedLanguages: payload.metadata?.supportedLanguages,
  };
};

const CAPTURE_INTERVAL_MS = 5 * 60 * 1000;
const DETECTION_SYNC_INTERVAL_MS = 30 * 1000;
const COOLDOWN_MS = 2000;

const DETECTION_THRESHOLDS = {
  webcam: 10,
  tab_switch: 3,
  keypress: 3,
  fullscreen_exit: 2,
};

const DETECTION_MESSAGES = {
  webcam:
    "Repeated webcam anomalies detected.\nEnsure you're alone and clearly visible. Further issues may end the test.",
  tab_switch:
    'Repeated tab switches detected.\nStay focused on the assessment window.',
  keypress:
    'Repeated shortcut usage detected.\nAvoid using function keys or shortcuts.',
  fullscreen_exit:
    'Repeated fullscreen exits detected.\nPlease remain in fullscreen mode.',
};

const PENALTY_KEY_MAP = {
  webcam: 'count_webcamdetect',
  tab_switch: 'count_tabswitch',
  keypress: 'count_keypress',
  fullscreen_exit: 'count_fullscreen_exit',
};

const PENALTY_TYPES = Object.keys(PENALTY_KEY_MAP);

const DETECTION_EVENT_TYPES = [
  'multiple_face',
  'face_not_visible',
  'cell_phone',
  'book',
  'laptop',
];

const DETECTION_COUNTS_STORAGE_KEY = 'webcam_detection_counts';
const NOTIFICATION_DURATION_MS = 10000;

const notificationContainerStyle = {
  position: 'fixed',
  top: '190px',
  right: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  zIndex: 1000,
  pointerEvents: 'none',
};

const notificationItemStyle = {
  minWidth: '260px',
  maxWidth: '320px',
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: '#1f2937',
  color: '#ffffffff',
  border: '1px solid #fbbf24',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.35)',
  fontSize: '14px',
  lineHeight: 1.4,
  pointerEvents: 'auto',
};

const examPageStyle = {
  backgroundColor: '#f5f7fb',
  position: 'relative',
  minHeight: '100vh',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const fullscreenButtonWrapperStyle = {
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  zIndex: 950,
};

const fullscreenButtonStyle = {
  padding: '9px 18px',
  borderRadius: '99px',
  fontSize: '0.90rem',
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: '#1f2937',
  border: '1px solid #fbbf24',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.35)',
  color: '#fff',
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalContentStyle = {
  backgroundColor: '#1f2937',
  padding: '40px',
  borderRadius: '12px',
  border: '2px solid #fbbf24',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  textAlign: 'center',
  maxWidth: '500px',
  width: '90%',
};

const modalTitleStyle = {
  color: '#fbbf24',
  fontSize: '1.5rem',
  fontWeight: 700,
  marginBottom: '16px',
};

const modalMessageStyle = {
  color: '#ffffff',
  fontSize: '1rem',
  lineHeight: 1.6,
  marginBottom: '32px',
};

const modalButtonStyle = {
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: '#fbbf24',
  border: 'none',
  color: '#1f2937',
  transition: 'transform 0.2s',
};

const blurredContentStyle = {
  filter: 'blur(8px)',
  pointerEvents: 'none',
  userSelect: 'none',
};

const createEmptyDetectionCounts = () =>
  DETECTION_EVENT_TYPES.reduce((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {});

const createPenaltySnapshot = (source = {}) =>
  PENALTY_TYPES.reduce((acc, key) => {
    const value = source[key];
    acc[key] = Number.isFinite(value) ? value : 0;
    return acc;
  }, {});

const ExamPage = () => {
  const history = useHistory();
  const {
    assessmentId,
    timer,
    loadNextQuestion,
    submitAnswer,
    refreshProgress,
    resetAssessment,
    uploadSnapshot,
    selfIntroSubmitted,
  } = useAssessment();

  
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const pageRef = useRef(null);

const formattedSection = question?.section
  ? question.section.charAt(0).toUpperCase() + question.section.slice(1)
  : '';

  const penaltiesRef = useRef(
    PENALTY_TYPES.reduce((acc, key) => {
      acc[key] = parseInt(sessionStorage.getItem(PENALTY_KEY_MAP[key]) || '0', 10);
      return acc;
    }, {})
  );
  const lastSyncedPenaltiesRef = useRef(createPenaltySnapshot(penaltiesRef.current));
  const alertCooldownsRef = useRef({
    multiple_face: 0,
    face_not_visible: 0,
    cell_phone: 0,
    book: 0,
    laptop: 0,
  });
  const detectionCountsRef = useRef(createEmptyDetectionCounts());
  const lastSyncedCountsRef = useRef(createEmptyDetectionCounts());
  const detectionSyncDirtyRef = useRef(false);
  const detectionSyncIntervalRef = useRef(null);
  const detectionSyncInFlightRef = useRef(false);
  const imageCaptureIntervalRef = useRef(null);
  const completedRef = useRef(false);
  const notificationTimersRef = useRef({});

  const detectionEnabled = Boolean(assessmentId && selfIntroSubmitted && !completedRef.current);

  const persistDetectionCounts = useCallback(() => {
    const storageKey = assessmentId
      ? `${DETECTION_COUNTS_STORAGE_KEY}_${assessmentId}`
      : DETECTION_COUNTS_STORAGE_KEY;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(detectionCountsRef.current));
    } catch (storageError) {
      console.error('Failed to persist detection counts', storageError);
    }
  }, [assessmentId]);

  useEffect(() => {
    const storageKey = assessmentId
      ? `${DETECTION_COUNTS_STORAGE_KEY}_${assessmentId}`
      : DETECTION_COUNTS_STORAGE_KEY;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) {
        detectionCountsRef.current = createEmptyDetectionCounts();
        lastSyncedCountsRef.current = createEmptyDetectionCounts();
        detectionSyncDirtyRef.current = false;
        return;
      }
      const parsed = JSON.parse(stored);
      detectionCountsRef.current = {
        ...createEmptyDetectionCounts(),
        ...(parsed && typeof parsed === 'object' ? parsed : {}),
      };
      lastSyncedCountsRef.current = createEmptyDetectionCounts();
      detectionSyncDirtyRef.current = true;
    } catch (restoreError) {
      console.error('Failed to restore detection counts', restoreError);
      detectionCountsRef.current = createEmptyDetectionCounts();
      lastSyncedCountsRef.current = createEmptyDetectionCounts();
      detectionSyncDirtyRef.current = true;
      sessionStorage.removeItem(storageKey);
    }
  }, [assessmentId]);

  useEffect(() => {
    lastSyncedPenaltiesRef.current = createPenaltySnapshot(penaltiesRef.current);
  }, [assessmentId]);

  useEffect(() => {
    return () => {
      Object.values(notificationTimersRef.current).forEach((timer) => clearTimeout(timer));
      notificationTimersRef.current = {};
    };
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
    const timer = notificationTimersRef.current[id];
    if (timer) {
      clearTimeout(timer);
      delete notificationTimersRef.current[id];
    }
  }, []);

  const pushNotification = useCallback(
    (message) => {
      if (!message) {
        return;
      }
      const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setNotifications((current) => [...current, { id, message }]);
      const timer = setTimeout(() => removeNotification(id), NOTIFICATION_DURATION_MS);
      notificationTimersRef.current[id] = timer;
    },
    [removeNotification]
  );

  const showDetectionAlert = useCallback(
    (type) => {
      const message = DETECTION_MESSAGES[type];
      if (!message) {
        return;
      }
      pushNotification(message);
    },
    [pushNotification]
  );

  const incrementPenalty = useCallback(
    (type) => {
      if (!DETECTION_THRESHOLDS[type]) {
        return;
      }
      penaltiesRef.current[type] = (penaltiesRef.current[type] || 0) + 1;
      const storageKey = PENALTY_KEY_MAP[type];
      if (storageKey) {
        sessionStorage.setItem(storageKey, String(penaltiesRef.current[type]));
      }
      if (
        penaltiesRef.current[type] !== 0 &&
        penaltiesRef.current[type] % DETECTION_THRESHOLDS[type] === 0
      ) {
        showDetectionAlert(type);
      }
      detectionSyncDirtyRef.current = true;
    },
    [showDetectionAlert]
  );

  const recordDetectionEvent = useCallback(
    (eventType) => {
      if (!DETECTION_EVENT_TYPES.includes(eventType)) {
        return;
      }
      detectionCountsRef.current[eventType] =
        (detectionCountsRef.current[eventType] || 0) + 1;
      detectionSyncDirtyRef.current = true;
      persistDetectionCounts();
    },
    [persistDetectionCounts]
  );

  const handlePredictions = useCallback(
    (predictions) => {
      const now = Date.now();
      const cooldowns = alertCooldownsRef.current;
      const personCount = predictions.filter((pred) => pred.class === 'person')
        .length;

      if (personCount > 1 && now > cooldowns.multiple_face) {
        recordDetectionEvent('multiple_face');
        incrementPenalty('webcam');
        cooldowns.multiple_face = now + COOLDOWN_MS;
      } else if (personCount === 0 && now > cooldowns.face_not_visible) {
        recordDetectionEvent('face_not_visible');
        incrementPenalty('webcam');
        cooldowns.face_not_visible = now + COOLDOWN_MS;
      }

      predictions.forEach((prediction) => {
        if (
          ['cell phone', 'book', 'laptop'].includes(prediction.class) &&
          now > cooldowns[prediction.class.replace(' ', '_')]
        ) {
          const eventType = prediction.class.replace(' ', '_');
          recordDetectionEvent(eventType);
          incrementPenalty('webcam');
          cooldowns[eventType] = now + COOLDOWN_MS;
        }
      });
    },
    [incrementPenalty, recordDetectionEvent]
  );

  const {
    status: detectionStatus,
    error: detectionError,
    videoElement: detectionVideoElement,
    start: startDetections,
    stop: stopDetections,
  } = useWebcamDetections({
    enabled: detectionEnabled,
    detectionInterval: 250,
    onDetections: handlePredictions,
  });

  const flushDetectionCounts = useCallback(
    async ({ force = false } = {}) => {
      if (!assessmentId || !selfIntroSubmitted) {
        return;
      }
      if (detectionSyncInFlightRef.current) {
        return;
      }

      const countsChanged = DETECTION_EVENT_TYPES.some(
        (type) => detectionCountsRef.current[type] !== lastSyncedCountsRef.current[type]
      );
      const penaltiesChanged = PENALTY_TYPES.some(
        (type) => penaltiesRef.current[type] !== lastSyncedPenaltiesRef.current[type]
      );

      if (!force && !detectionSyncDirtyRef.current && !countsChanged && !penaltiesChanged) {
        return;
      }

      detectionSyncInFlightRef.current = true;
      try {
        const payloadCounts = { ...detectionCountsRef.current };
        const payloadPenalties = createPenaltySnapshot(penaltiesRef.current);
        await uploadDetectionSummary(assessmentId, {
          counts: payloadCounts,
          penalties: payloadPenalties,
          recordedAt: new Date().toISOString(),
        });
        lastSyncedCountsRef.current = payloadCounts;
        lastSyncedPenaltiesRef.current = payloadPenalties;
        const countsShifted = DETECTION_EVENT_TYPES.some(
          (type) => detectionCountsRef.current[type] !== payloadCounts[type]
        );
        const penaltiesShifted = PENALTY_TYPES.some(
          (type) => penaltiesRef.current[type] !== payloadPenalties[type]
        );
        detectionSyncDirtyRef.current = countsShifted || penaltiesShifted;
      } catch (syncError) {
        console.error('Failed to sync detection counts', syncError);
        detectionSyncDirtyRef.current = true;
      } finally {
        detectionSyncInFlightRef.current = false;
      }
    },
    [assessmentId, selfIntroSubmitted]
  );

  const captureAndSendImage = useCallback(async () => {
    if (!assessmentId || detectionStatus !== 'running') {
      return;
    }
    const video = detectionVideoElement;
    if (!video || !video.srcObject || video.readyState < 2) {
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const base64Image = canvas.toDataURL('image/jpeg');
      await uploadSnapshot({
        image: base64Image,
        timestamp: new Date().toISOString(),
      });
    } catch (snapshotError) {
      console.error('Image capture failed', snapshotError);
    }
  }, [assessmentId, detectionStatus, detectionVideoElement, uploadSnapshot]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      incrementPenalty('tab_switch');
    }
  }, [incrementPenalty]);

  const handleWindowBlur = useCallback(() => {
    incrementPenalty('tab_switch');
  }, [incrementPenalty]);

  const handleFullscreenChange = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const active = Boolean(document.fullscreenElement);
    setIsFullscreen(active);
    if (!active && !completedRef.current) {
      setShowFullscreenModal(true);
      incrementPenalty('fullscreen_exit');
    }
  }, [incrementPenalty]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      const blockedKeys = new Set(['F12', 'F11', 'F5', 'Tab', 'Escape', 'Esc']);
      const blockedCtrlKeys = new Set([
        'c',
        'v',
        'x',
        'a',
        's',
        'p',
        'u',
        'i',
        'j',
        'k',
        'r',
        'f',
        'z',
        'y',
      ]);
      if (blockedKeys.has(event.key)) {
        incrementPenalty('keypress');
        event.preventDefault();
      }
      if (event.ctrlKey || event.metaKey) {
        const lowerKey = event.key.toLowerCase();
        if (blockedCtrlKeys.has(lowerKey)) {
          incrementPenalty('keypress');
          event.preventDefault();
        }
      }
    },
    [incrementPenalty]
  );

  const cleanupProctoring = useCallback(() => {
    if (imageCaptureIntervalRef.current) {
      clearInterval(imageCaptureIntervalRef.current);
      imageCaptureIntervalRef.current = null;
    }
    if (detectionSyncIntervalRef.current) {
      clearInterval(detectionSyncIntervalRef.current);
      detectionSyncIntervalRef.current = null;
    }
    (async () => {
      try {
        await flushDetectionCounts({ force: true });
      } catch (cleanupError) {
        console.error('Failed to flush detection data on cleanup', cleanupError);
      }
    })();
  }, [flushDetectionCounts]);

  const handleAssessmentComplete = useCallback(
    (message) => {
      if (!completedRef.current) {
        completedRef.current = true;
      }
      cleanupProctoring();
      if (typeof document !== 'undefined' && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      resetAssessment();
      history.replace({ pathname: '/thank-you', state: { message } });
    },
    [cleanupProctoring, history, resetAssessment]
  );

  const loadQuestion = useCallback(async () => {
    if (!assessmentId) {
      return;
    }
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const data = await loadNextQuestion();
      if (!data) {
        setQuestion(null);
        setInfo('No further questions are available right now.');
        return;
      }
      if (data.message && !data.section) {
        handleAssessmentComplete(data.message);
        return;
      }
      const normalized = normalizeQuestion(data);
      if (!normalized) {
        setInfo('Awaiting the next question.');
        setQuestion(null);
        return;
      }
      setQuestion(normalized);
      setAnswer(
        normalized.type === 'coding'
          ? { code: normalized.metadata?.starterCode || '', languageId: null }
          : ''
      );
    } catch (err) {
      const message =
        err?.response?.data?.detail?.message || err?.message || 'Unable to fetch question.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [assessmentId, handleAssessmentComplete, loadNextQuestion]);

  useEffect(() => {
    if (!assessmentId) {
      return undefined;
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    assessmentId,
    handleContextMenu,
    handleFullscreenChange,
    handleKeyDown,
    handleVisibilityChange,
    handleWindowBlur,
  ]);

  const enterFullscreen = useCallback(async () => {
    if (typeof document === 'undefined') {
      return;
    }
    const element = pageRef.current || document.documentElement;
    if (!element || document.fullscreenElement) {
      return;
    }
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      }
      setShowFullscreenModal(false);
      // Scroll to top after entering fullscreen
      setTimeout(() => {
        if (pageRef.current) {
          pageRef.current.scrollTop = 0;
        }
      }, 100);
    } catch (fullscreenError) {
      console.warn('Failed to enter fullscreen', fullscreenError);
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    setIsFullscreen(Boolean(document.fullscreenElement));
  }, []);

  useEffect(() => {
    if (!assessmentId) {
      return;
    }
    enterFullscreen();
  }, [assessmentId, enterFullscreen]);

  useEffect(() => {
    if (!assessmentId || !selfIntroSubmitted || completedRef.current) {
      stopDetections();
      return undefined;
    }
    let cancelled = false;
    const startAsync = async () => {
      try {
        await startDetections();
        if (cancelled) {
          stopDetections();
        }
      } catch (startError) {
        console.error('Failed to start webcam detections', startError);
      }
    };
    startAsync();
    return () => {
      cancelled = true;
      stopDetections();
    };
  }, [assessmentId, selfIntroSubmitted, startDetections, stopDetections]);

  useEffect(() => {
    if (!detectionError) {
      return;
    }
    const message =
      detectionError?.message ||
      'Unable to access your webcam. Please allow permissions and reload.';
    swal('Camera Error', message, 'error');
    flushDetectionCounts({ force: true });
  }, [detectionError, flushDetectionCounts]);

  useEffect(() => {
    if (!assessmentId || detectionStatus !== 'running') {
      return undefined;
    }
    captureAndSendImage();
    const id = setInterval(() => captureAndSendImage(), CAPTURE_INTERVAL_MS);
    imageCaptureIntervalRef.current = id;
    return () => {
      clearInterval(id);
      imageCaptureIntervalRef.current = null;
    };
  }, [assessmentId, captureAndSendImage, detectionStatus]);

  useEffect(() => {
    if (!assessmentId || detectionStatus !== 'running' || !selfIntroSubmitted) {
      return undefined;
    }
    flushDetectionCounts({ force: true });
    const id = setInterval(() => {
      flushDetectionCounts();
    }, DETECTION_SYNC_INTERVAL_MS);
    detectionSyncIntervalRef.current = id;
    return () => {
      clearInterval(id);
      detectionSyncIntervalRef.current = null;
    };
  }, [assessmentId, detectionStatus, flushDetectionCounts, selfIntroSubmitted]);

  useEffect(() => {
    if (!assessmentId || !selfIntroSubmitted || completedRef.current) {
      stopDetections();
    }
  }, [assessmentId, selfIntroSubmitted, stopDetections]);
  const submitAssessmentAnswer = useCallback(
    async (payload) => {
      setSubmitting(true);
      setError(null);
      try {
        await submitAnswer(payload);
        await flushDetectionCounts({ force: true });
        await refreshProgress();
        await loadQuestion();
      } catch (err) {
        const message =
          err?.response?.data?.detail?.message || err?.message || 'Unable to submit answer.';
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [flushDetectionCounts, loadQuestion, refreshProgress, submitAnswer]
  );

  const handleCodingEditorSubmit = useCallback(
    async ({ code, languageId }) => {
      if (!question) {
        return;
      }
      if (!code || !String(code).trim()) {
        setError('Please write code before submitting.');
        return;
      }
      if (!languageId) {
        setError('Please select a language.');
        return;
      }
      const payload = {
        section: question.section,
        questionId: question.questionId,
        answer: code,
        answerType: 'code',
        metadata: { ...question.metadata, languageId },
        aiPenalties: { ...penaltiesRef.current },
      };
      setAnswer({ code, languageId });
      await submitAssessmentAnswer(payload);
    },
    [question, submitAssessmentAnswer]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!question) {
        return;
      }

      if (question.type === 'coding') {
        const codingValue = answer && typeof answer === 'object' ? answer : { code: '', languageId: null };
        await handleCodingEditorSubmit(codingValue);
        return;
      }

      if (!answer || !String(answer).trim()) {
        setError('Please provide an answer before submitting.');
        return;
      }

      const payload = {
        section: question.section,
        questionId: question.questionId,
        answer,
        answerType: question.type === 'mcq' ? 'multiple_choice' : 'text',
        metadata: { ...question.metadata },
        aiPenalties: { ...penaltiesRef.current },
      };

      await submitAssessmentAnswer(payload);
    },
    [answer, handleCodingEditorSubmit, question, submitAssessmentAnswer]
  );

  useEffect(() => {
    if (!assessmentId) {
      return;
    }
    loadQuestion();
  }, [assessmentId, loadQuestion]);

  if (!assessmentId) {
    return <Redirect to="/instructions" />;
  }

  return (
    <div className="exam-page" ref={pageRef} style={examPageStyle}>
      {showFullscreenModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h2 style={modalTitleStyle}>Fullscreen Required</h2>
            <p style={modalMessageStyle}>
              You have exited fullscreen mode. To continue with your assessment,
              please click the button below to return to fullscreen.
            </p>
            <button
              type="button"
              onClick={enterFullscreen}
              style={modalButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              Go Fullscreen
            </button>
          </div>
        </div>
      )}
      <div style={showFullscreenModal ? blurredContentStyle : undefined}>
        {notifications.length > 0 && (
          <div style={notificationContainerStyle}>
            {notifications.map((item) => (
              <div key={item.id} style={notificationItemStyle}>
                {item.message}
              </div>
            ))}
          </div>
        )}
        <header className="exam-header">
        <div>
          <h1>Takyon Online Test</h1>
          {formattedSection && <span className="exam-section">Section: {formattedSection}</span>}
        </div>
        <div className="exam-timer">
          <span>Time remaining:</span>
          <strong>
            <CountdownTimer timer={timer} onExpire={() => handleAssessmentComplete('Time limit reached.')} />
          </strong>
        </div>
      </header>

      <main className="exam-main">
        {loading && <div className="exam-status">Loading question…</div>}
        {error && <div className="exam-error">{error}</div>}
        {info && <div className="exam-info">{info}</div>}
        {question && !loading && (
          <form onSubmit={handleSubmit} className="exam-form">
            <div className="exam-question-number">Question {question.questionNumber}</div>
            <QuestionRenderer
              question={question}
              value={answer}
              onChange={setAnswer}
              onSubmitAnswer={question.type === 'coding' ? handleCodingEditorSubmit : undefined}
            />
            <div className="exam-actions">
              <button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit answer'}
              </button>
            </div>
          </form>
        )}
      </main>
      <footer className="exam-footer" style={fullscreenButtonWrapperStyle}>
        <button
          type="button"
          onClick={enterFullscreen}
          disabled={isFullscreen}
          style={{
            ...fullscreenButtonStyle,
            opacity: isFullscreen ? 0.6 : 1,
            cursor: isFullscreen ? 'default' : 'pointer',
          }}
        >
          Go fullscreen
        </button>
      </footer>
      </div>
    </div>
  );
};

export default ExamPage;
